/**
 * Hook for fetching tooltip data for map visualizations.
 *
 * This hook abstracts the fetching of composition, posting data
 * for map tooltips across different features (CustomDash, TalentDiscovery).
 *
 * Note: Compensation data should be handled separately as it uses a different API
 *
 * @example
 * const tooltipData = useFetchTooltipData({
 *   primaryDimension: 'GEOGRAPHY',
 *   tooltipFeature,
 *   compositionFilters: { ...filters },
 *   postingFilters: { ...filters },
 * });
 */
import { format as d3Format } from 'd3-format';
import type { GeoJsonProperties } from 'geojson';
import { isNil } from 'lodash';
import { useQuery } from 'urql';

import {
  COMPOSITION_TOOLTIP,
  type CompositionV2Entity,
  type CompositionV2Filters,
  type CompositionV2FullSnapshotReq,
  Dimension1,
  PAY_V2_TOOLTIP_QUERY,
  type PayV2Entity,
  type PayV2EntResp,
  POSTING_TOOLTIP,
  PositionsEntityKind,
  type PostingFilters,
} from '@revelio/data-access';
import { formatterWithExchangeRate } from '@revelio/replots';

import { calculateMarketTightnessMeterValue } from '../../next/CustomDash/components/DataProvider/market-conditions/z-score/calculate-meter-value';
import type { DistributionData } from '../../next/CustomDash/components/DataProvider/types';
import { getDistributionDataFromPayV2 } from '../../next/CustomDash/components/DataProvider/utils/convert-compensation-data';
import { useMonthApiFilters } from '../../next/entity-summary/use-month-api-filters';
import { useCurrencyPreference } from '../user-preferences/hooks/useCurrencyPreference';

export interface TooltipData {
  header: string;
  headcount?: string;
  postings?: string;
  timeToFill?: string;
  salaryDist?: string[];
  marketTightness?: string;
  isLoading: boolean;
  height?: number;
  showSalaryData?: boolean;
}

export interface UseFetchTooltipDataParams {
  dim1: Dimension1;
  tooltipFeature: GeoJsonProperties | null;
  compositionEntities?: CompositionV2Entity[];
  compositionFilters?: CompositionV2Filters;
  postingFilters?: PostingFilters;
  skipComposition?: boolean;
  skipPosting: boolean; // Users who don't have Posting tab access should skip this query which will result in - postings
  skipCompensation?: boolean;
}

const formatInt = d3Format(',.0f');

export const useFetchTooltipData = ({
  dim1,
  tooltipFeature,
  compositionEntities = [],
  compositionFilters,
  postingFilters = {},
  skipComposition = false,
  skipPosting = false,
  skipCompensation = false,
}: UseFetchTooltipDataParams): TooltipData | null => {
  const { code: currencyCode, exchangeRate } = useCurrencyPreference();
  const currencyFormatter = formatterWithExchangeRate(
    currencyCode,
    exchangeRate
  );
  const hasGranularSelection =
    hasGranularGeoSelection(compositionFilters) ||
    hasGranularGeoSelection(postingFilters);
  const geoFilter = getTooltipGeoFilter(tooltipFeature, hasGranularSelection);
  const useTooltipEntity = dim1 === Dimension1.Geography && !isNil(geoFilter);

  const compositionEntitiesEffective = useTooltipEntity
    ? buildTooltipEntities(tooltipFeature, compositionEntities)
    : compositionEntities;

  const compositionWithTooltipGeoFilters = replaceGeographyFilters(
    compositionFilters,
    useTooltipEntity ? {} : geoFilter
  );

  const postingWithTooltipGeoFilters = replaceGeographyFilters(
    postingFilters,
    geoFilter
  );

  // Composition query
  const dateRangeApiFilters = useMonthApiFilters();

  const {
    raw: _raw,
    start_date: _start_date,
    end_date: _end_date,
    ...compositionFiltersForQuery
  } = compositionWithTooltipGeoFilters ?? {};

  const [{ data: compositionTooltipData, fetching: compositionFetching }] =
    useQuery({
      query: COMPOSITION_TOOLTIP,
      variables: {
        entities: compositionEntitiesEffective,
        filters: {
          ...compositionFiltersForQuery,
          snapshot_date: dateRangeApiFilters?.date_range_api_filters
            ?.end_date as string,
        },
      },
      pause: skipComposition || !tooltipFeature,
    });

  // Posting query
  const [{ data: postingData, fetching: postingFetching }] = useQuery({
    query: POSTING_TOOLTIP,
    variables: {
      dim1: dim1,
      filters: {
        ...postingWithTooltipGeoFilters,
        start_date:
          dateRangeApiFilters?.date_range_full_api_filters?.start_date,
        end_date: dateRangeApiFilters?.date_range_full_api_filters?.end_date,
        metric_mode: 'expected_hires', // the market tightness calculation requires this weighting to be accurate for the zIndex calculations
      },
    },
    pause: skipPosting || !tooltipFeature,
  });

  // Compensation query
  const [{ data: compensationData, fetching: compensationFetching }] = useQuery(
    {
      query: PAY_V2_TOOLTIP_QUERY,
      variables: {
        req: {
          entities: compositionEntitiesEffective as PayV2Entity[],
          filters: compositionWithTooltipGeoFilters,
        },
      },
      pause: skipCompensation || !tooltipFeature,
    }
  );

  const distributionData = getDistributionDataFromPayV2(
    compensationData?.pay_v2?.entities as PayV2EntResp[],
    'total_compensation'
  );

  // Early exit after hooks to keep hook ordering consistent
  if (!tooltipFeature) {
    return null;
  }

  // ===== COMPOSITION TOOLTIP VALUES =====
  const { headcount: headcountFmt, headcountNumber } = (() => {
    if (!compositionTooltipData) return { headcount: '-' };

    const entity = compositionTooltipData.composition_v2_tooltip?.entities?.[0];
    if (!entity) return { headcount: '-' };

    const headcounts = entity?.scaled?.headcount;
    const lastHeadcount = headcounts?.[headcounts.length - 1];
    const headcount = lastHeadcount?.value;
    if (!headcount) return { headcount: '-' };

    return { headcount: formatInt(headcount), headcountNumber: headcount };
  })();

  // ===== POSTING TOOLTIP VALUES =====
  const {
    postings: postingFmt,
    postingsNumber,
    timeToFill: ttfFmt,
    timeToFillNumber,
  } = (() => {
    const entity = postingData?.posting?.[0];
    if (!entity) {
      return {
        postings: '-',
        timeToFill: '-',
      };
    }

    const lastPosting = entity.category?.[entity.category.length - 1];
    const activePostings = lastPosting?.metrics?.active;
    const timeToFill = lastPosting?.metrics?.time_to_fill;

    return {
      postings: activePostings ? formatInt(activePostings) : '-',
      postingsNumber: activePostings,
      timeToFill: timeToFill ? formatInt(timeToFill) : '-',
      timeToFillNumber: timeToFill,
    };
  })();

  const salaryData =
    compositionTooltipData?.composition_v2_tooltip?.entities?.[0]?.scaled
      ?.salary || [];
  const yearStartWage = salaryData.length > 0 ? salaryData[0]?.value || 0 : 0;
  const yearEndWage =
    salaryData.length > 0 ? salaryData[salaryData.length - 1]?.value || 0 : 0;
  const wageGrowth =
    yearStartWage !== 0 ? (yearEndWage - yearStartWage) / yearStartWage : 0;
  const marketTightness = calculateMarketTightnessMeterValue({
    wageGrowth,
    demandSupply: Number(postingsNumber) / Number(headcountNumber),
    timeToFill: Number(timeToFillNumber),
  });

  return {
    header:
      (tooltipFeature.label || tooltipFeature.location) ?? 'Unknown Geography',
    headcount: headcountFmt,
    postings: postingFmt,
    timeToFill: ttfFmt,
    salaryDist: getPayPercentiles(distributionData, currencyFormatter),
    marketTightness: (marketTightness * 100).toString(),
    isLoading: compositionFetching || postingFetching || compensationFetching,
    showSalaryData: !skipCompensation,
  };
};

type GeographyFilterKeys = 'region' | 'country' | 'msa' | 'state' | 'city';
/**
 * Replaces any existing geography filter keys (region, country, msa, state, city) in the incoming
 * `filters` object with those provided via `geoFilter`.
 *
 * If `geoFilter` is `null`, the original filters are returned untouched.
 */
export const replaceGeographyFilters = <T extends Record<string, unknown>>(
  filters: T | undefined,
  geoFilter: Partial<Record<GeographyFilterKeys, unknown>> | null
): T => {
  if (!geoFilter) return (filters ?? {}) as T;

  const {
    region: _region,
    country: _country,
    msa: _msa,
    state: _state,
    city: _city,
    ...rest
  } = (filters ?? {}) as T;

  return {
    ...rest,
    ...geoFilter,
  } as T;
};

const getTooltipGeoFilter = (
  tooltipFeature: GeoJsonProperties | null,
  hasGranularSelection: boolean
):
  | (Pick<
      CompositionV2FullSnapshotReq['filters'],
      'country' | 'msa' | 'city' | 'state'
    > &
      Pick<PostingFilters, 'country' | 'msa' | 'city' | 'state'>)
  | null => {
  if (
    !tooltipFeature ||
    !('id' in tooltipFeature) ||
    !('type' in tooltipFeature)
  ) {
    return null;
  }
  if (tooltipFeature['type'] === 1) {
    if (hasGranularSelection) {
      return null;
    }
    return { country: [Number(tooltipFeature['id'])] };
  }

  if (tooltipFeature['type'] === 2) {
    return { msa: [Number(tooltipFeature['id'])] };
  }

  if (tooltipFeature['type'] === 3) {
    return { city: [Number(tooltipFeature['id'])] };
  }

  if (tooltipFeature['type'] === 4) {
    return { state: [Number(tooltipFeature['id'])] };
  }

  return null;
};

const hasGranularGeoSelection = (
  filters: CompositionV2Filters | PostingFilters | undefined | null
): boolean => {
  if (!filters) return false;

  const hasValues = (
    value: Array<number | string | null | undefined> | null | undefined
  ) => Array.isArray(value) && value.length > 0;

  return (
    hasValues(filters.msa) ||
    hasValues(filters.city) ||
    hasValues(filters.state)
  );
};

const buildTooltipEntities = (
  tooltipFeature: GeoJsonProperties | null,
  fallbackEntities: CompositionV2Entity[]
): CompositionV2Entity[] => {
  if (!tooltipFeature) return fallbackEntities;

  const entId = Number(tooltipFeature['id']);
  const type = tooltipFeature['type'];

  if (type === 1) {
    return [{ EntID: entId, EntType: PositionsEntityKind.Country }];
  }
  if (type === 2) {
    return [{ EntID: entId, EntType: PositionsEntityKind.Msa }];
  }
  if (type === 3) {
    return [{ EntID: entId, EntType: PositionsEntityKind.City }];
  }
  if (type === 4) {
    return [{ EntID: entId, EntType: PositionsEntityKind.State }];
  }

  return fallbackEntities;
};

const getPayPercentiles = (
  distributionData: DistributionData[],
  currencyFormatter: (value: number) => string
) => {
  if (!distributionData || distributionData.length === 0) return [] as string[];
  const first = distributionData[0];
  if (!first || !first.distribution || first.distribution.length === 0) {
    return [] as string[];
  }

  const salaries = first.distribution.map((d) => d.value);
  // Convert CDF ratios (0-1) to percentage scale (0-100) for easier comparison
  const cdf = first.distribution.map((d) => d.cdf * 100);

  const getPercentiles = ({
    salaries = [],
    cdf = [],
    percentiles,
  }: {
    salaries: number[];
    cdf: number[];
    percentiles: number[];
  }) => {
    return percentiles.map((percentile) => {
      let nearest = cdf[0];
      // @ts-expect-error noUncheckedIndexedAccess
      let smallestDiff = Math.abs(cdf[0] - percentile);
      for (let i = 1; i < cdf.length; i++) {
        // @ts-expect-error noUncheckedIndexedAccess
        const difference = Math.abs(cdf[i] - percentile);
        if (difference < smallestDiff) {
          smallestDiff = difference;
          nearest = cdf[i];
        }
      }
      // biome-ignore lint/complexity/useIndexOf: migrated from develop
      const cdfIndex = cdf.findIndex((item) => item === nearest);
      return cdfIndex === -1 ? 0 : salaries[cdfIndex] || 0;
    });
  };

  return getPercentiles({
    salaries,
    cdf,
    percentiles: [10, 50, 90],
  }).map((val) => currencyFormatter(val));
};
